/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, Injector, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { AddListComponent } from 'app/shared/components/add-list/add-list.component';
import { IconActionButtonEventMessage } from 'app/shared/components/icon-action-button-list/icon-action-button-list-message.model';

import { UsersService } from 'app/modules/configuration/users/user-management/services/users.service';
import { OrderSearchListService } from 'app/modules/order/order-search/order-search/services/order-search-list.service';
import { ComponentItem } from 'app/shared/base/aside/component-item';
import { FormMode } from 'app/shared/enumeration/form-mode';
import { Subject, takeUntil } from 'rxjs';
import { OrderListType } from '../models/order-list-type';
import { RefundFormComponent } from '../refund-form/refund-form.component';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { OrderSearchListItemViewModel } from '../models/order-search-list-item-view.model';
import { OrbitTableColumnConfig, OrbitTableConfig, OrbitTableListActionEvent, OrbitTablePageEvent, OrbitTableRowActionEvent, OrbitTableSortEvent } from 'app/shared/components/orbit-table/orbit-table.models';

@Component({
	selector: 'order-search-list',
	templateUrl: './order-search-list.component.html',
	styleUrls: ['./order-search-list.component.scss'],
	standalone: false,
})
export class OrderSearchListComponent extends AddListComponent {
	@Input() isOrganiserUser = false;
	@Input() customerId: number;
	@Input() showListActions = true;
	private ngUnsubscribe: Subject<boolean> = new Subject();

	get listType(): OrderListType {
		if (this.customerId) {
			return OrderListType.CustomerOrderList;
		}
		if (this.isOrganiserUser) {
			return OrderListType.OrganiserOrderList;
		}
		return OrderListType.DefaultOrderList;
	}

	get displayedColumns(): string[] {
		if (this.listType === OrderListType.CustomerOrderList) {
			return [
				'hasRefundedTickets',
				'orderNumber',
				'orderDate',
				'entryCount',
				'nonEntryCount',
				'glampingCount',
				'entryDetails',
				'nonEntryDetails',
				'glampingDetails',
				'paymentPlan',
				'paymentMethod',
				'event',
				'eventDate',
				'tickets',
				'organiser',
				'venue',
				'eventStatus',
				'genre',
				'faceValueTotal',
				'bookingFee',
				'venueLevyFee',
				'discount',
				'processingFee',
				'dispatchMethodFee',
				'cancellationProtectionFee',
				'charityDonationAmount',
				'orderTotal',
				'orbitProcessingFee',
				'orbitCancellationProtectionFee',
				'orbitServiceFee',
				'orbitDispatchMethodFee',
				'orbitReceives',
				'venueReceives',
				'organiserProcessingFee',
				'organiserTicketIncome',
				'organiserReceives',
				'partnerCancellationProtectionFee',
				'charityReceives',
				'discountCode',
				'membershipCode',
				'actions',
			];
		}

		if (this.listType === OrderListType.OrganiserOrderList) {
			return [
				'hasRefundedTickets',
				'orderNumber',
				'orderDate',
				'entryCount',
				'nonEntryCount',
				'glampingCount',
				'entryDetails',
				'nonEntryDetails',
				'glampingDetails',
				'customer',
				'paymentPlan',
				'paymentMethod',
				'event',
				'eventDate',
				'tickets',
				'organiser',
				'venue',
				'eventStatus',
				'genre',
				'orderTotal',
			];
		}

		return [
			'hasRefundedTickets',
			'orderNumber',
			'orderDate',
			'entryCount',
			'nonEntryCount',
			'glampingCount',
			'entryDetails',
			'nonEntryDetails',
			'glampingDetails',
			'customer',
			'paymentPlan',
			'paymentMethod',
			'event',
			'eventDate',
			'tickets',
			'organiser',
			'venue',
			'eventStatus',
			'genre',
			'faceValueTotal',
			'bookingFee',
			'venueLevyFee',
			'discount',
			'processingFee',
			'dispatchMethodFee',
			'cancellationProtectionFee',
			'charityDonationAmount',
			'orderTotal',
			'orbitProcessingFee',
			'orbitCancellationProtectionFee',
			'orbitServiceFee',
			'orbitDispatchMethodFee',
			'orbitReceives',
			'venueReceives',
			'organiserProcessingFee',
			'organiserTicketIncome',
			'organiserReceives',
			'partnerCancellationProtectionFee',
			'charityReceives',
			'discountCode',
			'membershipCode',
			'actions',
		];
	}
	constructor(
		private route: ActivatedRoute,
		public readonly listService: OrderSearchListService,
		private asideService: AsideService,
		private injector: Injector,
		protected usersService: UsersService,
		protected confirmationDialogService: ConfirmationDialogService
	) {
		super(listService);

		this.defaultSortProperty = 'orderDate';
		this.defaultSortDirection = 'desc';
		listService.loadState();
		this.loadAfterInit = false;
		this.configureLocalLoadingOverlay();
	}

	get tableConfig(): OrbitTableConfig<OrderSearchListItemViewModel> {
		return {
			ariaLabel: 'Orders', dashboard: false,
			columns: this.displayedColumns.filter(key => key !== 'actions').map(key => this.columnConfig(key)),
			rowHover: true,
			selection: { mode: 'disabled', rowId: row => row.itemId },
			rowActions: this.displayedColumns.includes('actions') ? [
				{ id: 'refund', label: 'Create Refund', icon: 'money_off', disabled: row => !row.dataModel.canRefund,
					tooltip: row => row.dataModel.canRefund ? 'Create Refund' : row.dataModel.cannotRefundMessage, ariaLabel: row => `Create refund for order ${row.dataModel.orderNumber}` },
				{ id: 'sendConfirmation', label: 'Send Confirmation Email', icon: 'send', visible: row => row.dataModel.canSendConfirmationEmail && this.usersService.userPermissions.isInternalOrbitUser,
					ariaLabel: row => `Send confirmation email for order ${row.dataModel.orderNumber}` },
			] : [],
			rowActionsVisibility: 'hover-focus',
			listActions: this.showListActions ? [{ id: 'export', label: 'Export', icon: 'cloud_download', variant: 'secondary', visible: () => !!this.listService.itemsCount }] : [],
			sorting: { enabled: true, mode: 'server', active: this.sortProperty, direction: this.sortDirection as 'asc' | 'desc', disableClear: true },
			pagination: { enabled: !!this.listService.itemsCount, mode: 'server', pageIndex: this.listService.paginatorIndex, pageSize: this.listService.ListRequest.pageSize, showFirstLastButtons: true },
			scroll: { stickyHeader: true, stickyFirstColumn: true, minWidth: this.listType === OrderListType.OrganiserOrderList ? '3000px' : '7200px' },
			resultSummary: { visible: true, businessObjectName: 'Order', businessObjectNamePlural: 'Orders', hint: 'Row actions appear on hover' },
			state: { emptyTitle: 'No orders found', emptyMessage: 'Try changing or clearing the filters.' },
		};
	}

	private columnConfig(key: string): OrbitTableColumnConfig<OrderSearchListItemViewModel> {
		const currencyColumns = new Set(['faceValueTotal', 'bookingFee', 'venueLevyFee', 'discount', 'processingFee', 'dispatchMethodFee', 'cancellationProtectionFee', 'charityDonationAmount', 'orderTotal', 'orbitProcessingFee', 'orbitCancellationProtectionFee', 'orbitServiceFee', 'orbitDispatchMethodFee', 'orbitReceives', 'venueReceives', 'organiserProcessingFee', 'organiserTicketIncome', 'organiserReceives', 'partnerCancellationProtectionFee', 'charityReceives']);
		const countColumns = new Set(['entryCount', 'nonEntryCount', 'glampingCount']);
		const sortableColumns = new Set(['orderNumber', 'orderDate', 'customer', 'paymentPlan', 'event', 'eventDate', 'organiser', 'venue', 'eventStatus']);
		const labels: Record<string, string> = {
			hasRefundedTickets: 'Refunded', orderNumber: 'Order Number', orderDate: 'Order Date', entryCount: 'Entry (Count)', nonEntryCount: 'Non Entry (Count)', glampingCount: 'Glamping (Count)',
			entryDetails: 'Entry (Details)', nonEntryDetails: 'Non Entry (Details)', glampingDetails: 'Glamping (Details)', customer: 'Customer', paymentPlan: 'Payment Plan', paymentMethod: 'Payment Method',
			event: 'Event', eventDate: 'Event Date', tickets: 'Tickets', organiser: 'Organiser', venue: 'Venue', eventStatus: 'Event Status', genre: 'Genre', faceValueTotal: 'Face Value Total (£)',
			bookingFee: 'Booking Fee (£)', venueLevyFee: 'Venue Levy Fee (£)', discount: 'Discount (£)', processingFee: 'Processing Fee (£)', dispatchMethodFee: 'Dispatch Method Fee (£)',
			cancellationProtectionFee: 'Cancellation Protection Fee (£)', charityDonationAmount: 'Charity Donation Amount (£)', orderTotal: 'Order Total (£)', orbitProcessingFee: 'Orbit Processing Fee (£)',
			orbitCancellationProtectionFee: 'Orbit Cancellation Protection Fee (£)', orbitServiceFee: 'Orbit Service Fee (£)', orbitDispatchMethodFee: 'Orbit Dispatch Method Fee (£)', orbitReceives: 'Orbit Receives (£)',
			venueReceives: 'Venue Receives (£)', organiserProcessingFee: 'Organiser Processing Fee (£)', organiserTicketIncome: 'Organiser Ticket Income (£)', organiserReceives: 'Organiser Receives (£)',
			partnerCancellationProtectionFee: 'Partner Cancellation Protection Fee (£)', charityReceives: 'Charity Receives (£)', discountCode: 'Discount Code', membershipCode: 'Membership Code',
		};
		const isCurrency = currencyColumns.has(key);
		const isCount = countColumns.has(key);
		return {
			key, label: labels[key] ?? key, value: row => row.dataModel[key], sortable: sortableColumns.has(key),
			sticky: key === 'orderNumber', align: isCurrency || isCount ? 'right' : key === 'hasRefundedTickets' ? 'center' : 'left',
			width: isCurrency ? '235px' : this.orderColumnWidth(key), tooltip: !isCurrency && !isCount && key !== 'hasRefundedTickets',
			format: isCurrency ? value => value == null ? '' : Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
				: key === 'orderDate' || key === 'eventDate' ? value => this.formatDate(value) : undefined,
		};
	}

	private orderColumnWidth(key: string): string {
		if (['entryDetails', 'nonEntryDetails', 'glampingDetails', 'paymentMethod', 'tickets', 'eventStatus', 'genre'].includes(key)) return '200px';
		if (['paymentPlan'].includes(key)) return '190px';
		if (['discountCode', 'membershipCode'].includes(key)) return '180px';
		if (['event', 'organiser', 'venue'].includes(key)) return '150px';
		if (['entryCount', 'nonEntryCount', 'glampingCount'].includes(key)) return '140px';
		if (key === 'orderNumber') return '120px';
		if (key === 'customer') return '110px';
		return '100px';
	}

	private formatDate(value: unknown): string {
		if (!value) return '';
		const date = new Date(value as string | number | Date);
		return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB');
	}

	get orderSearchListService() {
		return this.service as OrderSearchListService;
	}

	ngAfterViewInit() {
		this.asideService.asideClose.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
			if (data === 'update') {
				this.reloadList();
			}
		});
		super.ngAfterViewInit();
	}

	ngOnInit() {
		super.ngOnInit();
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next(true);
		this.ngUnsubscribe.complete();
		super.ngOnDestroy();
	}

	exportList() {
		this.listService.customerId = this.customerId;
		this.blockUI.start();
		this.listService
			.exportToExcel()
			.then(() => {
				this.blockUI.stop();
			})
			.catch((error) => {
				this.blockUI.stop();
			});
	}

	addNew() {}

	onSortChange(event: OrbitTableSortEvent): void { this.listService.setSort(event.active, event.direction); }
	onPageChange(event: OrbitTablePageEvent): void { this.listService.setPaging(event.pageSize, event.pageIndex + 1); }
	onListAction(event: OrbitTableListActionEvent): void { if (event.actionId === 'export') this.exportList(); }
	onRowAction(event: OrbitTableRowActionEvent<OrderSearchListItemViewModel>): void { this.doIconButtonAction(new IconActionButtonEventMessage(event.actionId, event.row.itemId)); }

	doIconButtonAction(message: IconActionButtonEventMessage) {
		switch (message.action) {
			case 'refund': {
				this.asideService.setAndOpen(new ComponentItem(RefundFormComponent, { itemId: message.data, formMode: FormMode.Create }), this.injector);
				break;
			}
			case 'sendConfirmation': {
				this.confirmationDialogService.openConfirmationDialog('Confirm Send', 'Are you sure you want to send this Booking Confirmation Email?', () => {
					this.blockUI.start();
					this.listService
						.sendOrderConfirmationEmail(message.data)
						.then(() => {})
						.finally(() => {
							this.blockUI.stop();
						});
				});

				break;
			}
			default: {
				console.log('Unsupported action: ' + message.action);
				break;
			}
		}
	}
}
